<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'next/icon/Icon.vue';
import RichTextEditor from 'dashboard/routes/dashboard/settings/components/RichTextEditor.vue';
import EventDocumentsAPI from 'dashboard/api/planning/eventDocuments';
import QuotePdfAttachment from './QuotePdfAttachment.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  eventId: { type: [String, Number], default: null },
  document: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue', 'sent', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const loading = ref(false);
const sending = ref(false);
const error = ref('');
const preview = ref(null);
const subject = ref('');
const htmlBody = ref('');

const FIELD_INPUT_CLASS =
  'reset-base h-10 w-full px-3 text-sm font-medium rounded bg-white dark:bg-slate-800 text-n-slate-12 outline outline-1 outline-n-slate-6 border-none';

const isRemind = computed(() =>
  ['pending_signature', 'viewed'].includes(props.document?.status)
);

const dialogTitle = computed(() =>
  isRemind.value
    ? t('PLANNING.SHOW.DOCUMENTS_REMIND_TITLE')
    : t('PLANNING.SHOW.DOCUMENTS_SEND_TITLE')
);

const confirmLabel = computed(() =>
  isRemind.value
    ? t('PLANNING.SHOW.DOCUMENTS_REMIND')
    : t('PLANNING.SHOW.DOCUMENTS_SEND_CONFIRM')
);

const channelLabel = computed(() => {
  if (!preview.value) return '';
  return t('PLANNING.SHOW.DOCUMENTS_SEND_VIA_PLATFORM');
});

const attachmentFilename = computed(() => {
  const doc = props.document;
  if (!doc) return t('PLANNING.SHOW.DOCUMENTS_SEND_ATTACHMENT');
  return doc.filename || doc.title || t('PLANNING.SHOW.DOCUMENTS_SEND_ATTACHMENT');
});

const htmlBodyText = computed(() =>
  htmlBody.value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const canSend = computed(() => {
  if (sending.value || loading.value || !preview.value) return false;
  if (!preview.value.recipient_email) return false;
  if (!subject.value?.trim()) return false;
  if (!htmlBodyText.value) return false;
  return true;
});

const plainToHtml = text => {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text
    .split(/\n{2,}/)
    .map(
      block =>
        `<p>${block
          .split('\n')
          .map(line => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
          .join('<br>')}</p>`
    )
    .join('');
};

const loadPreview = async () => {
  if (!props.eventId || !props.document?.id) return;
  loading.value = true;
  error.value = '';
  preview.value = null;
  try {
    const res = await EventDocumentsAPI.sendPreview(
      props.eventId,
      props.document.id
    );
    preview.value = res.data;
    subject.value = res.data.default_subject || '';
    htmlBody.value = plainToHtml(res.data.default_body || '');
  } catch (e) {
    error.value =
      e?.response?.data?.error ||
      t('PLANNING.SHOW.DOCUMENTS_SEND_PREVIEW_ERROR');
    preview.value = null;
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};

watch(
  () => props.modelValue,
  async val => {
    if (val) {
      await nextTick();
      dialogRef.value?.open();
      loadPreview();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

const handleSend = async () => {
  if (!canSend.value) return;
  sending.value = true;
  error.value = '';
  try {
    const res = await EventDocumentsAPI.sendDocument(
      props.eventId,
      props.document.id,
      {
        subject: subject.value,
        body: htmlBodyText.value,
        html_body: htmlBody.value,
      }
    );
    emit('sent', res.data);
    handleClose();
  } catch (e) {
    error.value =
      e?.response?.data?.error ||
      (isRemind.value
        ? t('PLANNING.SHOW.DOCUMENTS_REMIND_ERROR')
        : t('PLANNING.SHOW.DOCUMENTS_SEND_ERROR'));
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    :show-cancel-button="false"
    :show-confirm-button="false"
    :is-loading="sending"
    @close="handleClose"
  >
    <template #header>
      <div class="flex min-w-0 items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ dialogTitle }}
          </h3>
          <p v-if="channelLabel" class="mt-1 mb-0 text-sm text-n-slate-11">
            {{ channelLabel }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          type="button"
          :aria-label="t('PLANNING.EVENTS.CANCEL')"
          @click="handleClose"
        />
      </div>
    </template>

    <div class="flex flex-col gap-4 px-1 pb-2">
      <div v-if="loading" class="py-8 text-center text-sm text-n-slate-11">
        {{ t('PLANNING.SHOW.DOCUMENTS_SEND_LOADING') }}
      </div>

      <template v-else-if="preview">
        <div
          v-if="preview.link_valid_days"
          class="flex items-start gap-2 rounded-lg border border-n-slate-6 bg-n-slate-2 px-4 py-3 text-sm text-n-slate-11 dark:bg-slate-900/50"
        >
          <Icon icon="i-lucide-link" class="mt-0.5 size-4 shrink-0" />
          <p class="m-0">
            {{
              t('PLANNING.SHOW.DOCUMENTS_SEND_LINK_VALID', {
                days: preview.link_valid_days,
              })
            }}
          </p>
        </div>

        <div
          class="grid gap-3 rounded-lg border border-n-slate-6 bg-white p-4 dark:bg-slate-950"
        >
          <div class="grid gap-1 text-sm">
            <span class="text-n-slate-11">{{
              t('PLANNING.SHOW.QUOTE_EMAIL_FROM')
            }}</span>
            <span class="font-medium text-n-slate-12">{{
              t('PLANNING.SHOW.DOCUMENTS_SEND_VIA_PLATFORM')
            }}</span>
          </div>
          <div class="grid gap-1 text-sm">
            <span class="text-n-slate-11">{{
              t('PLANNING.SHOW.QUOTE_EMAIL_TO')
            }}</span>
            <span class="font-medium text-n-slate-12">
              {{ preview.recipient_name || '—' }}
              <span
                v-if="preview.recipient_email"
                class="font-normal text-n-slate-11"
              >
                &lt;{{ preview.recipient_email }}&gt;
              </span>
            </span>
          </div>
          <Input
            v-model="subject"
            :label="t('PLANNING.SHOW.DOCUMENTS_SEND_SUBJECT')"
            :custom-input-class="FIELD_INPUT_CLASS"
          />
          <div class="grid gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{
              t('PLANNING.SHOW.DOCUMENTS_SEND_BODY')
            }}</span>
            <RichTextEditor
              :model-value="htmlBody"
              show-toolbar
              :show-color-picker="false"
              :show-image-upload="false"
              output-format="html"
              min-height="14rem"
              max-height="28rem"
              @update:model-value="htmlBody = $event"
            />
          </div>
          <div class="border-t border-n-slate-6 pt-3">
            <QuotePdfAttachment :filename="attachmentFilename" />
          </div>
        </div>
      </template>

      <p v-if="error" class="m-0 text-sm text-red-600">{{ error }}</p>
    </div>

    <template #footer>
      <div class="flex w-full items-center justify-end gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('PLANNING.EVENTS.CANCEL')"
          :disabled="sending"
          type="button"
          @click="handleClose"
        />
        <Button
          color="blue"
          :label="confirmLabel"
          :is-loading="sending"
          :disabled="!canSend"
          type="button"
          @click="handleSend"
        />
      </div>
    </template>
  </Dialog>
</template>
